import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Site-admin "edit translations in place" mode. Builds on the key-debug mode
 * (see {@link import("./i18n-debug").useI18nDebug}): when key-debug is on and
 * the viewer is a site admin, every rendered message shows its dotted key and
 * becomes clickable, opening a dialog to override the text. Overrides are kept
 * per-device in localStorage (never sent to the backend) and can be exported as
 * partial `messages/<locale>.json` files to merge into the repo.
 *
 * This is deliberately client-only: it never mutates server state, so the
 * admin gate is a visibility gate, not a security boundary.
 */
export type Messages = Record<string, unknown>;

/** `{ [locale]: { [dottedKey]: newValue } }`. */
export type OverridesByLocale = Record<string, Record<string, string>>;

type I18nEditState = {
    overrides: OverridesByLocale;
    setOverride: (locale: string, key: string, value: string) => void;
    clearOverride: (locale: string, key: string) => void;
    clearLocale: (locale: string) => void;
    clearAll: () => void;
};

export const useI18nEdit = create<I18nEditState>()(
    persist(
        (set) => ({
            overrides: {},
            setOverride: (locale, key, value) =>
                set((s) => ({
                    overrides: {
                        ...s.overrides,
                        [locale]: { ...(s.overrides[locale] ?? {}), [key]: value },
                    },
                })),
            clearOverride: (locale, key) =>
                set((s) => {
                    const forLocale = { ...(s.overrides[locale] ?? {}) };
                    delete forLocale[key];
                    const next = { ...s.overrides };
                    if (Object.keys(forLocale).length === 0) delete next[locale];
                    else next[locale] = forLocale;
                    return { overrides: next };
                }),
            clearLocale: (locale) =>
                set((s) => {
                    const next = { ...s.overrides };
                    delete next[locale];
                    return { overrides: next };
                }),
            clearAll: () => set({ overrides: {} }),
        }),
        { name: "ha-i18n-overrides" },
    ),
);

function isPlainObject(value: unknown): value is Messages {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

/**
 * Collect the set of valid dotted key paths (leaves) of a messages tree, using
 * the same scheme as {@link import("./i18n-debug").keyifyMessages} — array
 * entries get a numeric index suffix (`Foo.bar.0`). Used to validate the text
 * under the cursor before treating it as a clickable key.
 */
export function flattenKeySet(
    messages: Messages,
    prefix = "",
    out: Set<string> = new Set(),
): Set<string> {
    for (const [key, value] of Object.entries(messages)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (Array.isArray(value)) {
            value.forEach((_, i) => out.add(`${path}.${i}`));
        } else if (isPlainObject(value)) {
            flattenKeySet(value, path, out);
        } else {
            out.add(path);
        }
    }
    return out;
}

/** Resolve a dotted key path to its string value in a messages tree. */
export function lookupKey(messages: Messages, dotted: string): string | undefined {
    let node: unknown = messages;
    for (const part of dotted.split(".")) {
        if (Array.isArray(node)) node = node[Number(part)];
        else if (isPlainObject(node)) node = node[part];
        else return undefined;
    }
    return typeof node === "string" ? node : undefined;
}

/**
 * Expand a flat `{ "A.b.c": "x" }` override map into a nested tree. Numeric
 * segments stay as string keys here; {@link applyOverrides} reconciles them
 * against arrays in the base tree.
 */
export function expandDotted(flat: Record<string, string>): Messages {
    const root: Messages = {};
    for (const [dotted, value] of Object.entries(flat)) {
        const parts = dotted.split(".");
        let node: Messages = root;
        for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i];
            if (!isPlainObject(node[part])) node[part] = {};
            node = node[part] as Messages;
        }
        node[parts[parts.length - 1]] = value;
    }
    return root;
}

/**
 * Deep-overlay an (expanded) override tree onto a base messages tree, returning
 * a new tree. Arrays in the base are preserved as arrays, with overlay entries
 * applied by numeric index; everything else merges key-by-key with the overlay
 * winning at the leaves.
 */
export function applyOverrides(base: unknown, overlay: unknown): unknown {
    if (!isPlainObject(overlay)) return overlay;
    if (Array.isArray(base)) {
        const result = [...base];
        for (const [key, value] of Object.entries(overlay)) {
            const idx = Number(key);
            if (Number.isInteger(idx)) result[idx] = applyOverrides(result[idx], value);
        }
        return result;
    }
    if (!isPlainObject(base)) return overlay;
    const result: Messages = { ...base };
    for (const [key, value] of Object.entries(overlay)) {
        result[key] = applyOverrides(result[key], value);
    }
    return result;
}

export type ExportFile = { locale: string; filename: string; json: string };

/**
 * Build one partial `<locale>.json` per locale that has at least one override,
 * containing only the changed keys (nested to mirror the message tree). Merge
 * these into the repo's `messages/<locale>.json`, then run `just sync-messages`.
 */
export function buildExportFiles(overrides: OverridesByLocale): ExportFile[] {
    return Object.entries(overrides)
        .filter(([, keys]) => Object.keys(keys).length > 0)
        .map(([locale, keys]) => ({
            locale,
            filename: `${locale}.json`,
            json: `${JSON.stringify(expandDotted(keys), null, 2)}\n`,
        }));
}
